import { IconSend, IconTrash } from '@/assets/images';
import { AppIcon } from '@/components';
import { getElapsedMs, getSession } from '@/features/chats/sub-features/audio-recorder/utils/recorderSessions';
import { useAudioRecorder } from '@/features/chats/sub-features/audio-recorder/viewmodel/useAudioRecorder';
import { useLanguage } from '@/localization/useLanguage';
import { millisToMinutesAndSeconds } from '@/shared/utils/mediaUtils';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import './AudioRecorder.scss';

const MAX_BARS = 35;
const SAMPLE_INTERVAL = 55; // ~18fps — smooth visuals, low CPU
const VOICE_BIN_START = 2;
const VOICE_BIN_END = 30;
const VOICE_BIN_COUNT = VOICE_BIN_END - VOICE_BIN_START;
const BAR_MIN_SCALE = 0.06;

const WAVEFORM_BARS_HTML = Array.from(
   { length: MAX_BARS },
   () => `<span class="ar-wave-bar" style="opacity:0;transform:scaleY(${BAR_MIN_SCALE})"></span>`,
).join('');

const AudioRecorder = ({ activeChatJid, onComplete }) => {
   const { t } = useLanguage();

   // DOM refs — all updates bypass React diffing
   const waveformRef = useRef(null);
   const timerTextRef = useRef(null);
   const blinkDotRef = useRef(null);

   // Stored RAF callback — shared between mount and resume effects (no duplication)
   const animateBarsRef = useRef(null);
   const lastSecRef = useRef(-1);

   // Mutable audio state — none of this triggers re-renders
   const pipeline = useRef({
      rafId: null,
      audioCtx: null,
      analyserNode: null,
      sourceNode: null,
      freqData: null,
      ampHistory: [],
      lastSampleTs: 0,
   });

   const {
      error,
      stream,
      canSend,
      tick,
      startRecording,
      stopRecording,
      cancelRecording,
      isPaused,
      /** pauseRecording,
      resumeRecording,
      limitReached,*/
   } = useAudioRecorder({ activeChatJid, onStop: onComplete });

   // ── Mount: inject bars, define RAF loop once, kick off recording ───────────
   useEffect(() => {
      const pl = pipeline.current;
      const waveformEl = waveformRef.current;

      waveformEl.innerHTML = WAVEFORM_BARS_HTML;
      const bars = waveformEl.children; // live HTMLCollection — zero re-allocation

      // Restore ampHistory from session (for returning paused recordings)
      const session = getSession(activeChatJid);
      if (session?.ampHistory?.length) {
         pl.ampHistory = [...session.ampHistory];
         const offset = MAX_BARS - pl.ampHistory.length;
         for (let i = 0; i < MAX_BARS; i++) {
            const bar = bars[i];
            if (i < offset) {
               bar.style.opacity = '0';
            } else {
               bar.style.opacity = '1';
               bar.style.transform = `scaleY(${pl.ampHistory[i - offset]})`;
            }
         }
      }

      // Define once, stored in ref so the pause/resume effect can restart it
      animateBarsRef.current = ts => {
         // Timer: compute from timestamps, update DOM only when the ROUNDED second
         // changes (not the floored one) — otherwise a value like 59.6s would keep
         // showing "0:59" for the rest of that second instead of flipping to "1:00"
         // right when millisToMinutesAndSeconds would actually round up to it.
         const ms = tick();
         const roundedSec = Math.round(ms / 1000);
         if (roundedSec !== lastSecRef.current) {
            lastSecRef.current = roundedSec;
            if (timerTextRef.current) timerTextRef.current.textContent = millisToMinutesAndSeconds(ms);
         }

         // Waveform: original logic, throttled at SAMPLE_INTERVAL
         const { analyserNode, freqData, ampHistory } = pl;

         if (analyserNode && ts - pl.lastSampleTs >= SAMPLE_INTERVAL) {
            pl.lastSampleTs = ts;
            analyserNode.getByteFrequencyData(freqData);

            let sum = 0;
            for (let i = VOICE_BIN_START; i < VOICE_BIN_END; i++) sum += freqData[i];
            const amp = Math.min(1, Math.max(BAR_MIN_SCALE, sum / (VOICE_BIN_COUNT * 128)));

            ampHistory.push(amp);
            if (ampHistory.length > MAX_BARS) ampHistory.shift();

            const offset = MAX_BARS - ampHistory.length;
            for (let i = 0; i < MAX_BARS; i++) {
               const bar = bars[i];
               if (i < offset) {
                  if (bar.style.opacity !== '0') bar.style.opacity = '0';
               } else {
                  bar.style.opacity = '1';
                  bar.style.transform = `scaleY(${ampHistory[i - offset]})`;
               }
            }
         }

         pl.rafId = requestAnimationFrame(animateBarsRef.current);
      };

      // Fresh session → start recording; Returning → hook already seeded state
      if (!stream) startRecording();
      pl.rafId = requestAnimationFrame(animateBarsRef.current);

      return () => {
         cancelAnimationFrame(pl.rafId);
         pl.rafId = null;

         // Preserve waveform in session for next mount
         const sess = getSession(activeChatJid);
         if (sess) sess.ampHistory = [...pl.ampHistory];

         pl.sourceNode?.disconnect();
         if (pl.audioCtx?.state !== 'closed') {
            pl.audioCtx?.close();
         }
         pl.audioCtx = null;
         pl.analyserNode = null;
      };
   }, []); // eslint-disable-line react-hooks/exhaustive-deps

   // ── Stream: wire mic → analyser (fresh AudioContext each mount) ───────────
   useEffect(() => {
      if (!stream) return;
      const pl = pipeline.current;

      // Always create fresh AudioContext (previous is closed on unmount)
      const AudioCtx = window.AudioContext || window['webkitAudioContext'];
      pl.audioCtx = new AudioCtx();
      pl.analyserNode = pl.audioCtx.createAnalyser();
      pl.analyserNode.fftSize = 256;
      pl.analyserNode.smoothingTimeConstant = 0.65;
      pl.freqData = new Uint8Array(pl.analyserNode.frequencyBinCount);

      pl.sourceNode?.disconnect();
      pl.sourceNode = pl.audioCtx.createMediaStreamSource(stream);
      pl.sourceNode.connect(pl.analyserNode);

      return () => pl.sourceNode?.disconnect();
   }, [stream]);

   // ── Pause/Resume: halt RAF → 0 CPU; restart immediately on resume ─────────
   useEffect(() => {
      const pl = pipeline.current;
      if (isPaused) {
         // The limit can now be enforced by a setInterval that runs independently of
         // this rAF loop (so it still fires when the tab is hidden). When that
         // interval — not this loop's own tick() call — is what actually crosses the
         // limit and flips isPaused, the last text this loop painted can be one
         // second behind the true stop point. Do one final sync so the frozen
         // display always matches the real elapsed time before halting.
         const ms = tick();
         if (timerTextRef.current) timerTextRef.current.textContent = millisToMinutesAndSeconds(ms);
         lastSecRef.current = Math.round(ms / 1000);
         cancelAnimationFrame(pl.rafId);
         pl.rafId = null;
      } else if (!pl.rafId && animateBarsRef.current) {
         pl.rafId = requestAnimationFrame(animateBarsRef.current);
      }
   }, [isPaused, tick]);

   // ── Timer text: set immediately on mount for returning sessions ───────────
   useLayoutEffect(() => {
      const session = getSession(activeChatJid);
      if (timerTextRef.current && session) {
         const ms = getElapsedMs(session);
         timerTextRef.current.textContent = millisToMinutesAndSeconds(ms);
         lastSecRef.current = Math.round(ms / 1000);
      }
   }, [activeChatJid]);

   // ── Dot: useLayoutEffect → class updated synchronously before paint ───────
   useLayoutEffect(() => {
      if (blinkDotRef.current) blinkDotRef.current.className = isPaused ? 'ar-dot ar-dot--paused' : 'ar-dot';
   }, [isPaused]);

   const handleCancel = useCallback(() => {
      cancelRecording();
      onComplete?.(null);
   }, [cancelRecording, onComplete]);

   /** const handleTogglePause = useCallback(() => {
      if (isPaused) resumeRecording();
      else pauseRecording();
   }, [isPaused, pauseRecording, resumeRecording]);*/

   if (error) {
      return (
         <div className="ar-container ct-flex ct-aiCenter ct-jcCenter">
            <div className="ar-panel ar-panel--error ct-flex ct-aiCenter ct-flex1 ct-gap12">
               <span className="ar-error-text ct-flex1">{error}</span>
               <button className="ar-btn ar-btn--trash ct-flexCenter ct-cursor" onClick={handleCancel}>
                  <IconTrash className="ar-btn-icon" />
               </button>
            </div>
         </div>
      );
   }

   return (
      <div className="ar-container ct-flex ct-aiCenter ct-jcCenter ct-gap16">
         <div className="ar-panel ct-flex ct-aiCenter ct-flex1 ct-gap10">
            <div className="ct-flex1" />

            {/** {!limitReached && (
               <button
                  className="ar-btn ct-flexCenter ct-cursor"
                  onClick={handleTogglePause}
                  aria-label={isPaused ? t('common.PLAY') : t('common.PAUSE')}
               >
                  {isPaused ? <PlayIcon className="ar-btn-icon" /> : <PauseIcon className="ar-btn-icon" />}
               </button>
            )} */}

            <div className="ar-waveform ct-flex ct-aiCenter" ref={waveformRef} />

            <div className="ar-timer ct-flex ct-aiCenter ct-gap6">
               <span className="ar-dot" ref={blinkDotRef} />
               <span className="ar-time" ref={timerTextRef}>
                  00:00
               </span>
            </div>

            <button
               className="ar-btn ar-btn--trash ct-flexCenter ct-cursor"
               onClick={handleCancel}
               aria-label={t('chat.CANCEL_RECORDING')}
            >
               <IconTrash className="ar-btn-icon" />
            </button>
         </div>

         <button
            className="ar-btn ar-btn--send ct-flexCenter ct-cursor"
            onClick={stopRecording}
            disabled={!canSend}
            aria-label={t('chat.SEND_VOICE_MESSAGE')}
         >
            <AppIcon name="send" icon={<IconSend />} className="ar-btn-icon" />
         </button>
      </div>
   );
};

AudioRecorder.propTypes = {
   activeChatJid: PropTypes.string.isRequired,
   onComplete: PropTypes.func.isRequired,
};

export default AudioRecorder;
